import React, { useEffect, useMemo, useState } from 'react';
import './TrainingPage.css';

const formatDateTime = (value) => {
  if (!value) return 'TBD';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'TBD';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildCalendarUrl = (session) => {
  const start = session.startTime?.toDate ? session.startTime.toDate() : new Date(session.startTime);
  const end = session.endTime?.toDate ? session.endTime.toDate() : new Date(session.endTime);
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const format = (date) =>
    date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: session.title || 'Online Training Session',
    dates: `${format(start)}/${format(end)}`,
    details: session.description || 'Online training session.',
    location: session.meetLink || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const TrainingPage = ({ firebase }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebase) return undefined;
    const unsubscribe = firebase.db
      .collection('academySessions')
      .orderBy('startTime', 'asc')
      .onSnapshot(
        (snapshot) => {
          const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setSessions(items);
          setLoading(false);
        },
        () => setLoading(false),
      );

    return () => unsubscribe();
  }, [firebase]);

  const now = Date.now();
  const upcoming = useMemo(
    () =>
      sessions.filter((session) => {
        const start = session.startTime?.toDate ? session.startTime.toDate() : new Date(session.startTime);
        return start && !Number.isNaN(start.getTime()) && start.getTime() >= now;
      }),
    [sessions, now],
  );
  const past = useMemo(
    () =>
      sessions.filter((session) => {
        const start = session.startTime?.toDate ? session.startTime.toDate() : new Date(session.startTime);
        return start && !Number.isNaN(start.getTime()) && start.getTime() < now;
      }),
    [sessions, now],
  );

  return (
    <div className="training-page">
      <div className="training-header">
        <div>
          <h2>Online Training</h2>
          <p>Upcoming sessions scheduled by HQ. Add them to your calendar below.</p>
        </div>
      </div>

      <div className="training-section">
        <h3>Upcoming Sessions</h3>
        {loading ? (
          <div className="waiting-card">Loading sessions…</div>
        ) : upcoming.length === 0 ? (
          <div className="waiting-card">No upcoming sessions yet.</div>
        ) : (
          <div className="session-list">
            {upcoming.map((session) => (
              <div className="session-card" key={session.id}>
                <div>
                  <h4>{session.title || 'Online Training Session'}</h4>
                  <p>{formatDateTime(session.startTime)}</p>
                  {session.description ? <p className="session-desc">{session.description}</p> : null}
                </div>
                <div className="session-actions">
                  {session.meetLink ? (
                    <a href={session.meetLink} target="_blank" rel="noreferrer">
                      Join Meet
                    </a>
                  ) : (
                    <span className="muted">Meet link pending</span>
                  )}
                  {buildCalendarUrl(session) ? (
                    <a href={buildCalendarUrl(session)} target="_blank" rel="noreferrer">
                      Add to Calendar
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="training-section">
        <h3>Past Sessions</h3>
        {past.length === 0 ? (
          <div className="waiting-card">No past sessions.</div>
        ) : (
          <div className="session-list past">
            {past.map((session) => (
              <div className="session-card" key={session.id}>
                <div>
                  <h4>{session.title || 'Online Training Session'}</h4>
                  <p>{formatDateTime(session.startTime)}</p>
                </div>
                <div className="session-actions">
                  {buildCalendarUrl(session) ? (
                    <a href={buildCalendarUrl(session)} target="_blank" rel="noreferrer">
                      Add to Calendar
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingPage;
